import type { DatasetConfig, DatasetSearchParams } from '../types';

/**
 * Maps search payload to tile endpoint query params.
 * Iterates the dataset filter config instead of hardcoding every field.
 * @param {DatasetSearchParams | undefined} payload - The current search payload.
 * @param {DatasetConfig} config - The dataset configuration containing filter definitions.
 * @return {string} URL-encoded query string for the tile endpoint.
 */
export const buildTileFilterParams = (
  payload: DatasetSearchParams | undefined,
  config: DatasetConfig
): string => {
  if (!payload) return '';

  const params = new URLSearchParams();

  // Text search
  if (payload.text) params.set('text', String(payload.text));

  // Iterate filter fields from config
  for (const field of config.filters) {
    if (
      field.type === 'text' ||
      field.type === 'checkbox' ||
      field.type === 'radio' ||
      field.type === 'survey-area'
    ) {
      const val = payload[field.param];
      if (val) params.set(field.param, String(val));
    } else if (
      field.type === 'picklist' ||
      field.type === 'multi-select' ||
      field.type === 'multi-select-list'
    ) {
      const arr = payload[field.param] as string[] | undefined;
      if (arr?.length) params.set(field.param, arr.join(','));
    } else if (field.type === 'tree-table') {
      const parents = payload[field.param] as string[] | undefined;
      if (parents?.length) params.set(field.param, parents.join(','));
      const children = payload[field.childParam] as string[] | undefined;
      if (children?.length) params.set(field.childParam, children.join(','));
    } else if (field.type === 'multi-select-text-search-combo') {
      const arr = payload[field.param] as string[] | undefined;
      if (arr?.length) params.set(field.param, arr.join(','));
      const textVal = payload[field.textParam];
      if (textVal) params.set(field.textParam, String(textVal));
    } else if (field.type === 'date-range' || field.type === 'numeric-range') {
      const minVal = payload[field.minParam];
      if (minVal) params.set(field.minParam, String(minVal));
      const maxVal = payload[field.maxParam];
      if (maxVal) params.set(field.maxParam, String(maxVal));
    }
  }

  // County Recording plotting mode. A sidebar control, not a modal filter, so
  // it's set explicitly here rather than iterated from config.filters (which
  // would also surface it as a filter chip).
  if (payload.plot_by_county) params.set('plot_by_county', 'true');

  // Which fields the text search matches (Areas opts into the wide search). Part of
  // the dataset's default payload, not a filter, so set here like plot_by_county.
  if (payload.search_scope)
    params.set('search_scope', String(payload.search_scope));

  // Polygon → base64url encode as poly_geojson
  if (payload.polygon_geojson) {
    const json = JSON.stringify(payload.polygon_geojson);
    const b64 = btoa(json)
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=+$/, '');
    params.set('poly_geojson', b64);
  }

  // Bounding box is intentionally excluded from tile params.
  // Tiles are already spatially constrained by their z/x/y coordinates.
  // Including bbox would invalidate every cached tile on pan, causing
  // all features to disappear and re-render.

  return params.toString();
};
